import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials } from '../types';
import { login as appwriteLogin, logout as appwriteLogout, getCurrentUser } from '../services/authService';
import { checkAuth } from '../services/appwriteClient';
import InvalidRoleDialog from '../components/common/InvalidRoleDialog';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  showInvalidRoleDialog: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  closeInvalidRoleDialog: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvalidRoleDialog, setShowInvalidRoleDialog] = useState(false);

  useEffect(() => {
    // Check for existing session
    const checkSession = async () => {
      try {
        // First check if we have a valid Appwrite session
        const isAuthenticated = await checkAuth();
        if (isAuthenticated) {
          const currentUser = await getCurrentUser();
          if (currentUser) {
            // Validate user role on session restore
            if (currentUser.role !== 'admin' && currentUser.role !== 'teacher') {
              // Invalid role, log out and show dialog
              await logout();
              setShowInvalidRoleDialog(true);
              return;
            }
            
            setUser(currentUser);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      setLoading(true);
      setError(null);
      
      const userData = await appwriteLogin(credentials);
      setUser(userData);
      setIsAuthenticated(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      
      // Check if it's an invalid role error
      if (errorMessage === 'INVALID_ROLE') {
        setShowInvalidRoleDialog(true);
      }
      
      // Check if it's a session already active error
      if (errorMessage.includes('Session already active')) {
        // Force logout to clear the session
        await logout();
        // Try login again
        try {
          const userData = await appwriteLogin(credentials);
          setUser(userData);
          setIsAuthenticated(true);
          setError(null);
        } catch (retryError) {
          const retryErrorMessage = retryError instanceof Error ? retryError.message : 'Login failed';
          setError(retryErrorMessage);
          if (retryErrorMessage === 'INVALID_ROLE') {
            setShowInvalidRoleDialog(true);
          }
        }
        return;
      }
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const closeInvalidRoleDialog = () => {
    setShowInvalidRoleDialog(false);
  };

  const logout = async () => {
    try {
      setLoading(true);
      await appwriteLogout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      loading, 
      error, 
      showInvalidRoleDialog,
      login, 
      logout,
      closeInvalidRoleDialog
    }}>
      {children}
      <InvalidRoleDialog 
        isOpen={showInvalidRoleDialog} 
        onClose={closeInvalidRoleDialog} 
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};